import React, { useRef, useCallback } from 'react';

/**
 * DayScheduleEditor — visual editor for a single day's send window(s).
 * Renders an hour ruler (0–24h) with one or more draggable "slot" bands over it,
 * plus removable slot pills below. Built to later host an analytics density chart
 * behind the bands (open/click-by-hour) without layout change — the chart just
 * fills the same track area.
 *
 * Value model: slots = [{ start:'HH:MM', end:'HH:MM' }] (minutes granularity).
 * onChange(slots) fires on every edit.
 *
 * `densityCurve` (optional, future): array of 24 numbers 0..1 → drawn as a faint
 * area behind the bands to show when opens/clicks peak.
 */

const toMin = (t) => { const [h, m] = String(t || '0:0').split(':').map(Number); return (h || 0) * 60 + (m || 0); };
const toHHMM = (min) => {
  const m = Math.max(0, Math.min(1440, Math.round(min / 5) * 5)); // snap to 5-min
  const hh = String(Math.floor(m / 60) % 24).padStart(2, '0');
  const mm = String(m % 60).padStart(2, '0');
  return `${hh}:${mm}`;
};
const fmt12 = (t) => {
  const [h, m] = String(t).split(':').map(Number);
  const ap = h < 12 ? 'AM' : 'PM'; const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m).padStart(2,'0')} ${ap}`;
};

export default function DayScheduleEditor({ slots = [], onChange, densityCurve = null }) {
  const trackRef = useRef(null);
  const drag = useRef(null); // { index, edge:'start'|'end'|'move', startX, orig }
  const [dragInfo, setDragInfo] = React.useState(null); // { index, edge } while dragging

  const normalized = slots.length ? slots : [{ start: '09:00', end: '17:00' }];

  const pxToMin = useCallback((clientX) => {
    const el = trackRef.current; if (!el) return 0;
    const rect = el.getBoundingClientRect();
    const frac = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    return frac * 1440;
  }, []);

  const onDown = (index, edge) => (e) => {
    e.preventDefault(); e.stopPropagation();
    drag.current = { index, edge, startX: e.clientX, orig: { ...normalized[index] } };
    setDragInfo({ index, edge });
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  // Bounds this slot can occupy without overlapping neighbors (in minutes).
  const neighborBounds = (index) => {
    let lo = 0, hi = 1440;
    normalized.forEach((s, i) => {
      if (i === index) return;
      const a = toMin(s.start), b = toMin(s.end);
      // treat each other slot as [a,b]; push our bounds to avoid it
      if (b <= toMin(normalized[index].start) && b > lo) lo = b;   // slot ends before us → floor
      if (a >= toMin(normalized[index].end) && a < hi) hi = a;     // slot starts after us → ceiling
    });
    return { lo, hi };
  };

  const onMove = (e) => {
    const dg = drag.current; if (!dg) return;
    const next = normalized.map(s => ({ ...s }));
    const s = next[dg.index];
    const oStart = toMin(dg.orig.start), oEnd = toMin(dg.orig.end);
    const deltaMin = pxToMin(e.clientX) - pxToMin(dg.startX);
    const { lo, hi } = neighborBounds(dg.index);
    if (dg.edge === 'start') {
      let v = oStart + deltaMin;
      v = Math.max(lo, Math.min(v, oEnd - 15));   // ≥15min, not past neighbor floor
      s.start = toHHMM(v);
    } else if (dg.edge === 'end') {
      let v = oEnd + deltaMin;
      v = Math.min(hi, Math.max(v, oStart + 15)); // ≥15min, not past neighbor ceiling
      s.end = toHHMM(v);
    } else { // move whole band, clamped inside [lo,hi]
      const len = oEnd - oStart;
      let ns = oStart + deltaMin;
      ns = Math.max(lo, Math.min(hi - len, ns));
      s.start = toHHMM(ns); s.end = toHHMM(ns + len);
    }
    onChange(next);
  };

  const onUp = () => {
    drag.current = null;
    setDragInfo(null);
    window.removeEventListener('mousemove', onMove);
    window.removeEventListener('mouseup', onUp);
  };

  // Find the largest free gap between existing slots for a new one.
  const findGap = () => {
    const occ = normalized.map(s => [toMin(s.start), toMin(s.end)]).filter(([a,b]) => b > a).sort((x,y)=>x[0]-y[0]);
    let best = null, cursor = 0;
    for (const [a,b] of occ) { if (a - cursor >= 60 && (!best || a-cursor > best[1]-best[0])) best = [cursor, a]; cursor = Math.max(cursor, b); }
    if (1440 - cursor >= 60 && (!best || 1440-cursor > best[1]-best[0])) best = [cursor, 1440];
    return best; // [start,end] minutes or null
  };
  const gap = findGap();

  const addSlot = () => {
    if (!gap) return; // no room → button disabled
    const [gs, ge] = gap;
    const start = gs, end = Math.min(ge, gs + 120); // default up to 2h
    onChange([...normalized, { start: toHHMM(start), end: toHHMM(end) }]);
  };
  const removeSlot = (i) => {
    const next = normalized.filter((_, idx) => idx !== i);
    onChange(next.length ? next : [{ start: '09:00', end: '17:00' }]);
  };
  const setSlot = (i, patch) => {
    const cand = { ...normalized[i], ...patch };
    const a = toMin(cand.start), b = toMin(cand.end);
    // Reject if this would overlap any other slot (ignore wrap for typed input).
    const overlaps = normalized.some((s, idx) => {
      if (idx === i) return false;
      const x = toMin(s.start), y = toMin(s.end);
      return a < y && b > x; // standard interval overlap
    });
    if (overlaps || b <= a) return; // ignore invalid edit
    onChange(normalized.map((s, idx) => idx === i ? cand : s));
  };

  const pct = (min) => `${(min / 1440) * 100}%`;

  return (
    <div className="space-y-2">
      {/* Track: hour ruler + density curve + draggable bands */}
      <div ref={trackRef} className="relative h-14 rounded bg-gray-50 border border-gray-200 select-none">
        {/* density curve (future analytics) */}
        {Array.isArray(densityCurve) && densityCurve.length > 1 && (
          <svg viewBox="0 0 240 56" preserveAspectRatio="none" className="absolute inset-0 w-full h-full">
            <polygon
              points={`0,56 ${densityCurve.map((v, i) => `${(i / (densityCurve.length - 1)) * 240},${56 - Math.max(0, Math.min(1, v)) * 50}`).join(' ')} 240,56`}
              fill="#9ca3af" opacity="0.15" />
          </svg>
        )}
        {/* hour gridlines — every hour, heavier at 6/12/18 */}
        {Array.from({ length: 23 }, (_, k) => k + 1).map(h => (
          <div key={h} className={`absolute top-0 bottom-0 border-l ${h % 6 === 0 ? 'border-gray-300' : 'border-gray-200/70'}`} style={{ left: `${(h / 24) * 100}%` }} />
        ))}
        {/* slot bands */}
        {normalized.map((s, i) => {
          const a = toMin(s.start), b = toMin(s.end);
          const wraps = b <= a;
          const segs = wraps ? [[a, 1440], [0, b]] : [[a, b]];
          const widthPct = ((wraps ? (1440 - a + b) : (b - a)) / 1440) * 100;
          const rangeLabel = `${fmt12(s.start)} – ${fmt12(s.end)}`;
          const showCentered = widthPct > 26; // only when band is wide enough (B)
          return segs.map(([x0, x1], k) => (
            <div key={`${i}-${k}`}
              onMouseDown={onDown(i, 'move')}
              className="absolute top-0 bottom-0 bg-emerald-500/20 border-x-2 border-emerald-600 cursor-grab active:cursor-grabbing flex items-center justify-center"
              style={{ left: pct(x0), width: pct(x1 - x0) }}>
              {/* B: centered range label (first segment only, when wide enough) */}
              {k === 0 && showCentered && (
                <span className="text-[10px] font-semibold text-emerald-700 whitespace-nowrap pointer-events-none px-1">{rangeLabel}</span>
              )}
              {k === 0 && !wraps && (
                <>
                  <div onMouseDown={onDown(i, 'start')} className="absolute left-[-4px] top-1/2 -translate-y-1/2 w-2 h-6 bg-emerald-600 rounded cursor-ew-resize" />
                  {/* C: drag tooltip on the active start handle */}
                  {dragInfo && dragInfo.index === i && dragInfo.edge !== 'end' && (
                    <span className="absolute left-0 -translate-x-1/2 -top-6 bg-emerald-700 text-white text-[9px] px-1.5 py-0.5 rounded whitespace-nowrap pointer-events-none">{fmt12(s.start)}</span>
                  )}
                </>
              )}
              {(k === segs.length - 1) && !wraps && (
                <>
                  <div onMouseDown={onDown(i, 'end')} className="absolute right-[-4px] top-1/2 -translate-y-1/2 w-2 h-6 bg-emerald-600 rounded cursor-ew-resize" />
                  {/* C: drag tooltip on the active end handle */}
                  {dragInfo && dragInfo.index === i && dragInfo.edge !== 'start' && (
                    <span className="absolute right-0 translate-x-1/2 -top-6 bg-emerald-700 text-white text-[9px] px-1.5 py-0.5 rounded whitespace-nowrap pointer-events-none">{fmt12(s.end)}</span>
                  )}
                </>
              )}
            </div>
          ));
        })}
      </div>
      {/* hour axis labels */}
      <div className="flex justify-between text-[9px] text-gray-400 px-0.5">
        <span>12a</span><span>6a</span><span>12p</span><span>6p</span><span>12a</span>
      </div>

      {/* slot pills — precise edit + remove */}
      <div className="flex items-center gap-2 flex-wrap">
        {normalized.map((s, i) => {
          const overnight = toMin(s.end) <= toMin(s.start);
          return (
            <div key={i} className="inline-flex items-center gap-2 bg-white border border-gray-200 rounded-lg pl-2 pr-1.5 py-1 shadow-sm">
              <span className="flex items-center justify-center w-4 h-4 rounded bg-emerald-100 text-emerald-700 text-[9px] font-semibold flex-shrink-0">{i + 1}</span>
              <div className="flex items-center gap-1 text-[11px] text-gray-700">
                <input type="time" value={s.start} onChange={(e) => setSlot(i, { start: e.target.value })}
                  className="bg-gray-50 border border-gray-200 rounded px-1 py-0.5 focus:outline-none focus:ring-1 focus:ring-emerald-400" />
                <span className="text-gray-300">–</span>
                <input type="time" value={s.end} onChange={(e) => setSlot(i, { end: e.target.value })}
                  className="bg-gray-50 border border-gray-200 rounded px-1 py-0.5 focus:outline-none focus:ring-1 focus:ring-emerald-400" />
              </div>
              {overnight && (
                <span className="text-[9px] font-medium text-amber-600 bg-amber-50 border border-amber-200 rounded px-1 py-0.5" title="This window runs past midnight">overnight</span>
              )}
              {normalized.length > 1 && (
                <button onClick={() => removeSlot(i)} title="Remove this window"
                  className="flex items-center justify-center w-5 h-5 rounded text-gray-400 hover:text-red-500 hover:bg-red-50 flex-shrink-0">
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              )}
            </div>
          );
        })}
        <button onClick={addSlot} disabled={!gap}
          title={gap ? 'Add another sending window' : 'No open time left in the day'}
          className={`inline-flex items-center gap-1 text-[11px] font-medium border border-dashed rounded-lg px-2.5 py-1.5 transition-colors ${gap ? 'border-emerald-300 text-emerald-600 hover:bg-emerald-50' : 'border-gray-200 text-gray-300 cursor-not-allowed'}`}>
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
          Add window
        </button>
      </div>
    </div>
  );
}
